import React from 'react';
import { useTranslation } from 'react-i18next';
import { ResponsivePie } from '@nivo/pie';

import { useDashboardContext } from 'src/shared/contexts';
import LoadingIndicator from 'src/shared/loading-indicator';
import { NivoTheme } from 'src/theming/theme-nivo/nivo-theme-context';

import { EsaStateAmounts } from '../common';
import { DashboardCard } from '../dashboard-card/dashboard-card';

function mapAmountsToCakeData(
    amounts: EsaStateAmounts
): { id: string; label: string; value: number }[] | undefined | null {
    if (amounts == null) {
        return undefined;
    }

    const data = [];

    if (amounts.emptyAmount > 0) {
        data.push({
            id: 'emptyAmount',
            label: 'Leer',
            value: amounts.emptyAmount,
        });
    }

    if (amounts.zeroAmount > 0) {
        data.push({
            id: 'zeroAmount',
            label: 'Null',
            value: amounts.zeroAmount,
        });
    }

    if (amounts.readyForWithdrawalAmount > 0) {
        data.push({
            id: 'readyForWithdrawal',
            label: 'Auszahlungsreif',
            value: amounts.readyForWithdrawalAmount,
        });
    }

    if (amounts.overallRegulationAmount > 0) {
        data.push({
            id: 'overallRegulationAmount',
            label: 'Gesamtregulierung',
            value: amounts.overallRegulationAmount,
        });
    }

    if (amounts.noPaymentAmount > 0) {
        data.push({
            id: 'noPaymentAmount',
            label: 'Keine Zahlung',
            value: amounts.noPaymentAmount,
        });
    }

    if (amounts.inquiryToVnAmount > 0) {
        data.push({
            id: 'inquiryToVnAmount',
            label: 'Rückfragen an VN',
            value: amounts.inquiryToVnAmount,
        });
    }

    if (amounts.saAvailableAmount > 0) {
        data.push({
            id: 'saAvailableAmount',
            label: 'SA angefordert',
            value: amounts.saAvailableAmount,
        });
    }

    if (amounts.saRequestedAmount > 0) {
        data.push({
            id: 'saRequestedAmount',
            label: 'SA liegt vor',
            value: amounts.saRequestedAmount,
        });
    }

    if (amounts.partitionAgreementAmount > 0) {
        data.push({
            id: 'partitionAgreementAmount',
            label: 'Teilungsabkommen',
            value: amounts.partitionAgreementAmount,
        });
    }

    if (amounts.partialPaymentAmount > 0) {
        data.push({
            id: 'partialPaymentAmount',
            label: 'Teilzahlung',
            value: amounts.partialPaymentAmount,
        });
    }

    if (amounts.documentsToVuRaAmount > 0) {
        data.push({
            id: 'documentsToVuRaAmount',
            label: 'Unterlagen an VU/RA',
            value: amounts.documentsToVuRaAmount,
        });
    }

    if (amounts.unknownAmount > 0) {
        data.push({
            id: 'unknownAmount',
            label: 'Unbekannt',
            value: amounts.unknownAmount,
        });
    }

    return data.length > 0 ? data : null;
}

interface Props {
    nivoTheme: NivoTheme;
    title: string;
    editMode: boolean;
    amounts: EsaStateAmounts;
    isShown?: boolean;
    handleChangeShown?: (name: string, value: boolean) => void;
    name?: string;
}

export const DashboardDamagesByState = ({
    nivoTheme,
    title,
    editMode,
    isShown,
    handleChangeShown,
    name,
    amounts,
}: Props): JSX.Element => {
    const { t } = useTranslation(['dashboard']);

    const { isGetDashboardDataLoading } = useDashboardContext();

    const data = mapAmountsToCakeData(amounts);

    return (
        <DashboardCard
            header={title}
            subHeader={t('dashboard:damages.lastDamage')}
            editMode={editMode}
            isShown={isShown}
            handleChangeShown={handleChangeShown}
            name={name}
        >
            {isGetDashboardDataLoading && <LoadingIndicator />}
            {!isGetDashboardDataLoading && data === null && (
                <div
                    style={{
                        display: 'flex',
                        height: '100%',
                        justifyContent: 'center',
                        alignItems: 'center',
                        textAlign: 'center',
                    }}
                >
                    {t('emptyMsg')}
                </div>
            )}
            {!isGetDashboardDataLoading && data && (
                <ResponsivePie
                    data={data}
                    theme={nivoTheme.theme}
                    colors={nivoTheme.themedColors}
                    isInteractive={false}
                    margin={{ top: 50, right: 50, bottom: 50, left: 50 }}
                    activeOuterRadiusOffset={8}
                    borderWidth={1}
                    innerRadius={0.2}
                    padAngle={10}
                    arcLabelsTextColor={{ theme: 'textColor' }}
                    arcLabelsRadiusOffset={0.7}
                    arcLinkLabel='label'
                    arcLinkLabelsColor={{ from: 'color' }}
                />
            )}
        </DashboardCard>
    );
};
